/**
 * Seating chart stores tables under keys like `table-39` while `table.id` is the
 * number shown in the center of each circle (may be customized, e.g. 808080).
 */

export function buildSeatingTableKeyToDisplayIdMap(seatingAssignment) {
	const tables = seatingAssignment?.tables;
	if (!tables || typeof tables !== 'object') return {};
	const out = {};
	for (const [storageKey, table] of Object.entries(tables)) {
		if (table && table.id != null && `${table.id}`.trim() !== '') {
			out[storageKey] = String(table.id);
		}
	}
	return out;
}

/** Merge maps from multiple seating rows for the same day (site may have history rows). */
export function mergeSeatingTableDisplayMaps(maps) {
	const merged = {};
	for (const m of maps || []) {
		if (m && typeof m === 'object') Object.assign(merged, m);
	}
	return merged;
}

/**
 * If `storedTableId` is a seating storage key present in the map, return the chart number.
 * Otherwise return the stored value (already a display id or legacy text).
 */
export function resolveCustomerTableIdForDisplay(storedTableId, keyToDisplayMap) {
	if (storedTableId == null) return '';
	const s = String(storedTableId).trim();
	if (!s) return '';
	if (keyToDisplayMap && Object.prototype.hasOwnProperty.call(keyToDisplayMap, s)) {
		return keyToDisplayMap[s];
	}
	return s;
}

/** True when value is a seating JSON storage key like `table-39` (not a chart center label). */
export function isSeatingStorageTableKey(tableStr) {
	const s = String(tableStr || '').trim();
	return /^table-\d+$/.test(s);
}

/**
 * Extract trailing seat letter (A–H, case-insensitive) from stored values like `38C`,
 * `T102-F`, or a lone `G`.
 */
export function extractSeatLetterFromStoredSeating(seatStored) {
	const s = String(seatStored || '').trim().replace(/\s+/g, '');
	if (!s) return '';
	const dash = s.match(/-([A-Ha-h])$/i);
	if (dash) return dash[1].toUpperCase();
	if (/^[A-Ha-h]$/i.test(s)) return s.toUpperCase();
	const tail = s.match(/([A-Ha-h])$/i);
	if (tail) return tail[1].toUpperCase();
	return '';
}

/** Legacy bad data: a lone "1" is not a seat code — treat as empty so real seating / chart format wins. */
export function scrubBareOneSeatStr(value) {
	const t = String(value ?? '').trim();
	return t === '1' ? '' : t;
}

/**
 * Prefer `seat_number` when it encodes a chart seat (letter A–H); otherwise use `seating`
 * so bogus values like seat_number "1" do not hide "38C" in legacy `seating`.
 */
export function pickRawSeatStorageForChart(customer) {
	if (!customer) return '';
	const snRaw = `${customer.seat_number ?? ''}`.trim();
	const stRaw = `${customer.seating ?? ''}`.trim();
	const hasSeatLetter = (s) => !!extractSeatLetterFromStoredSeating(s);
	if (hasSeatLetter(snRaw)) return scrubBareOneSeatStr(snRaw) || snRaw;
	if (hasSeatLetter(stRaw)) return scrubBareOneSeatStr(stRaw) || stRaw;
	return scrubBareOneSeatStr(snRaw || stRaw);
}

/**
 * Chart-style seat label: `{displayTable}{letter}` (e.g. display table `102` + internal `38C` → `102C`).
 * When `tableDisplayId` is still a storage key (e.g. before the map loads), returns raw `seatStored`.
 */
export function formatSeatDisplayLikeSeatingChart(tableDisplayId, seatStored) {
	const table = String(tableDisplayId || '').trim();
	const raw = scrubBareOneSeatStr(String(seatStored || '').trim());
	if (!raw && !table) return '';
	if (isSeatingStorageTableKey(table)) return raw || '';
	const letter = extractSeatLetterFromStoredSeating(raw);
	if (table && letter) return `${table}${letter}`;
	return raw || '';
}

function idEqualsLooseSeatCustomer(a, b) {
	const sa = `${a ?? ''}`.trim();
	const sb = `${b ?? ''}`.trim();
	if (!sa || !sb) return false;
	if (sa === sb) return true;
	const la = sa.toLowerCase();
	const lb = sb.toLowerCase();
	if (la === lb) return true;
	if (sa.length === 24 && sb.length === 24 && /^[a-f0-9]+$/i.test(sa) && /^[a-f0-9]+$/i.test(sb)) return la === lb;
	return false;
}

function needleIdsForChartCustomerMatch(customer) {
	if (!customer) return [];
	const needles = [];
	const add = (v) => {
		if (v == null || v === '') return;
		let x = v;
		if (typeof x?.toHexString === 'function') x = x.toHexString();
		const s = `${x}`.trim();
		if (s && !needles.includes(s)) needles.push(s);
	};
	add(customer.id);
	add(customer._id);
	return needles;
}

/** Customers assigned to a seat (supports legacy single customerId and `customers` array). */
export function getSeatAssignedCustomers(seat) {
	if (!seat) return [];
	if (Array.isArray(seat.customers) && seat.customers.length > 0) {
		return seat.customers
			.map((c) => ({
				customerId: `${c?.customerId ?? c?.customer_id ?? c?.id ?? ''}`.trim(),
				customerName: `${c?.customerName ?? c?.customer_name ?? c?.name ?? ''}`.trim(),
			}))
			.filter((c) => c.customerId);
	}
	const id = `${seat.customerId ?? seat.customer_id ?? ''}`.trim();
	if (!id) return [];
	return [{
		customerId: id,
		customerName: `${seat.customerName ?? seat.customer_name ?? ''}`.trim(),
	}];
}

export function seatIncludesCustomerId(seat, customerId) {
	const needle = `${customerId ?? ''}`.trim();
	if (!needle) return false;
	return getSeatAssignedCustomers(seat).some((c) => idEqualsLooseSeatCustomer(c.customerId, needle));
}

/**
 * Chart name for a seat: first assigned customer who is not absent on today's routes.
 * If several are not absent, the first in assignment order. If none, the first assigned.
 */
export function pickDisplayedSeatCustomer(seat, notAbsentCustomerIds) {
	const assigned = getSeatAssignedCustomers(seat);
	if (!assigned.length) return null;
	const presentSet = new Set(
		[...(notAbsentCustomerIds instanceof Set ? notAbsentCustomerIds : (notAbsentCustomerIds || []))]
			.map((id) => `${id}`)
	);
	const present = assigned.filter((c) => presentSet.has(`${c.customerId}`));
	return present[0] || assigned[0];
}

/**
 * Merge today's `SeatingService.getAll` rows and find this customer's assigned seat.
 * Returns display table id and full label (e.g. `102` and `102H`), or `null` if not on the chart.
 */
export function chartTableAndSeatFromLiveSeatingRows(seatingRows, customer, keyToDisplayMap) {
	if (!customer || !Array.isArray(seatingRows) || !seatingRows.length) return null;
	const mergedTables = {};
	for (const row of seatingRows) {
		const t = row?.seating_assignment?.tables;
		if (t && typeof t === 'object') Object.assign(mergedTables, t);
	}
	const needles = needleIdsForChartCustomerMatch(customer);
	if (!needles.length) return null;

	for (const [storageKey, tbl] of Object.entries(mergedTables)) {
		if (!tbl) continue;
		const displayCenter = resolveCustomerTableIdForDisplay(storageKey, keyToDisplayMap);
		for (const seat of tbl.seats || []) {
			if (!needles.some((n) => seatIncludesCustomerId(seat, n))) continue;
			const sidToken = `${seat?.id ?? ''}`.trim();
			const nameTok = `${seat?.name ?? ''}`.trim();
			const letter = extractSeatLetterFromStoredSeating(sidToken || nameTok);
			if (letter && displayCenter && !isSeatingStorageTableKey(displayCenter)) {
				return { tableDisplay: displayCenter, fullSeat: `${displayCenter}${letter}` };
			}
		}
	}
	return null;
}
